import { useEffect, useRef, useState } from 'react';

/**
 * Poster that silently loops the MP4 on hover. Loads the file only after
 * the pointer settles, and only one card plays at a time.
 */

type StopFn = () => void;
let stopActive: StopFn | null = null;

function takeSlot(stop: StopFn) {
  if (stopActive && stopActive !== stop) stopActive();
  stopActive = stop;
}

function dropSlot(stop: StopFn) {
  if (stopActive === stop) stopActive = null;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function HoverPreview({
  src,
  poster,
  disabled,
}: {
  src?: string | null;
  poster?: string | null;
  disabled?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const enterTimer = useRef(0);
  const leaveTimer = useRef(0);
  const hovering = useRef(false);
  const [armed, setArmed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const canPlay = !!src && !disabled && !prefersReducedMotion();

  const haltRef = useRef(() => {});
  haltRef.current = () => {
    window.clearTimeout(enterTimer.current);
    const v = videoRef.current;
    if (v) {
      v.pause();
      try { v.currentTime = 0; } catch { /* ignore */ }
    }
    setPlaying(false);
  };
  const slotStop = useRef(() => { haltRef.current(); });

  useEffect(() => {
    if (!armed || !hovering.current) return;
    const v = videoRef.current;
    if (!v) return;
    takeSlot(slotStop.current);
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.loop = true;
    void v.play().catch(() => {});
  }, [armed]);

  useEffect(() => () => {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
    dropSlot(slotStop.current);
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
  }, []);

  return (
    <div
      className="absolute inset-0"
      onPointerEnter={() => {
        if (!canPlay) return;
        hovering.current = true;
        window.clearTimeout(leaveTimer.current);
        window.clearTimeout(enterTimer.current);
        enterTimer.current = window.setTimeout(() => setArmed(true), 90);
      }}
      onPointerLeave={() => {
        hovering.current = false;
        window.clearTimeout(enterTimer.current);
        haltRef.current();
        dropSlot(slotStop.current);
        leaveTimer.current = window.setTimeout(() => setArmed(false), 700);
      }}
    >
      {poster ? (
        <img
          src={poster}
          alt=""
          className={`h-full w-full object-cover transition duration-300 ${playing ? 'opacity-0' : 'opacity-100'}`}
        />
      ) : (
        <div className="h-full w-full bg-black" />
      )}
      {armed && src ? (
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="auto"
          className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition duration-300 ${
            playing ? 'opacity-100' : 'opacity-0'
          }`}
          onPlaying={() => setPlaying(true)}
        />
      ) : null}
    </div>
  );
}
